/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { ChangeDetectionStrategy, ChangeDetectorRef, Component, NgZone, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import {
  AbstractFormComponent,
  BuilderNavigationService,
  CommonActions,
  FormActionPipelines,
  FormFlowState,
} from '@gh/builder-shared';
import { SpecTemplateClauses } from '@gh/builder-shared-data';
import {
  ActionFlow,
  AppRouter,
  FormStateSnapshot,
  getFormStateSnapshotValue,
  getRouteInitData,
} from '@gh/common-shared';
import { TranslationService } from '@gh/core-mls';
import {
  DialogService,
  FormMode,
  NotificationService,
  WaitingService,
  yesAnswer,
  yesNoConfirmation,
} from '@gh/core-ui';
import {
  createDefaultTreeOptions,
  expandNodeById,
  goToNodeByPath,
  treeOptionsEnableDragging,
} from '@gh/core-ui-ext/tree';
import { FocusedCell, GridComponent } from '@gh/core-ui-grid';
import { filter$, first$, map$, mapTo$, switchMap$ } from '@gh/rx/operators';
import { ITreeState, TREE_ACTIONS, TreeComponent } from 'angular-tree-component';
// tslint:disable-next-line:no-submodule-imports
import { ITreeNode } from 'angular-tree-component/dist/defs/api';
import { negate } from 'lodash';
import { Observable } from 'rxjs';
import { JobSpecTemplateDetailsBusinessService } from '../../resources/job-spec-template-details-business.service';
import {
  JobSpecTemplateLevel,
  JstClause,
  JstDetails,
  JstMasterInfo,
  JstParagraph,
  JstParagraphInfo,
  JstSection,
  JstSectionInfo,
  jstSelectionToTreePath,
} from '../../resources/job-spec-template.model';
import { JobSpecTemplateDetailsFormService } from './job-spec-template-details-form.service';

@Component({
  selector: 'gh-job-spec-template-details',
  template: require('./job-spec-template-details.component.html'),
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    JobSpecTemplateDetailsFormService,
  ],
})
export class JobSpecTemplateDetailsComponent extends AbstractFormComponent<JstDetails> implements OnInit {
  title: string;

  treeOptions = createDefaultTreeOptions({
    idField: 'id',
    childrenField: 'sections',
    allowDrag: true,
    allowDrop: (node, { parent }) => node.level === parent.level + 1,
    nodeClass: (node) => node.level === 1 ? 'node-expandable' : '',
    // We disable selection on click and handle this behavior yourself.
    // Selection on click is not suitable when click on buttons.
    actionMapping: {
      mouse: {
        click: TREE_ACTIONS.ACTIVATE,
      },
    },
  });

  treeState: ITreeState = {};

  @ViewChild(TreeComponent, { static: true }) tree: TreeComponent;

  clauseFocusedCell$: Observable<Maybe<FocusedCell>>;

  constructor(private route: ActivatedRoute,
              private changeDetectorRef: ChangeDetectorRef,
              private translations: TranslationService,
              private waitings: WaitingService,
              private notifications: NotificationService,
              private businessService: JobSpecTemplateDetailsBusinessService,
              public form: JobSpecTemplateDetailsFormService,
              private ngZone: NgZone,
              private dialogService: DialogService,
              private pipelines: FormActionPipelines,
              private commonActions: CommonActions,
              private navigationService: BuilderNavigationService,
              private router: AppRouter) {
    super();
  }

  get modeTitle(): string {
    return {
      [FormMode.CreateMode]: 'lbl_create_specification_template',
      [FormMode.EditMode]: 'lbl_edit_specification_template',
      [FormMode.ViewMode]: 'lbl_specification_template',
    }[this.form.mode];
  }

  get isForRfq(): boolean {
    return this.form.value.isForRfq;
  }

  get masterInfo(): FormStateSnapshot<JstMasterInfo> {
    return this.form.value.masterInfo;
  }

  get paragraphs(): JstParagraph[] {
    return this.form.value.paragraphs;
  }

  get isParagraphSelected(): boolean {
    return this.form.selection.level === JobSpecTemplateLevel.Paragraph;
  }

  get isSectionSelected(): boolean {
    return this.form.selection.level === JobSpecTemplateLevel.Section;
  }

  get currentParagraph(): Maybe<JstParagraph> {
    return this.form.selection.paragraph;
  }

  get currentSection(): Maybe<JstSection> {
    return this.form.selection.section;
  }

  get completeFlow(): ActionFlow<FormFlowState<JstDetails>> {
    return this.pipelines.complete({
      execute: this.commonActions.execute((state: FormFlowState<JstDetails>) => this.businessService.save(state.value)),
      finish: (state: FormFlowState<JstDetails>) => {
        const { value } = state;
        this.form.resetDirty();
        if (this.form.createMode) {
          this.router.switchTo(['/setup/job-admin/job-spec-templates', value.specTemplateId]);
        } else {
          this.form.merge(value);
        }
      },
    });
  }

  ngOnInit(): void {
    const { details } = getRouteInitData(this.route);

    this.form.selectionChanges.pipe(
      map$(jstSelectionToTreePath),
      filter$(Boolean),
      // wait while all processing of new/removed nodes is completed by tree component
      switchMap$((path) => this.ngZone.onMicrotaskEmpty.pipe(first$(), mapTo$(path))))
      .subscribe((path: any) => {
        this.treeState = goToNodeByPath(this.treeState, path);
        this.changeDetectorRef.markForCheck();
      });

    this.clauseFocusedCell$ = this.form.selectionChanges.pipe(map$((selection) => selection.clauseFocusedCell));

    this.form.build(details);

    this.treeOptions = treeOptionsEnableDragging(this.treeOptions, this.form.editable);

    this.updateTitle();

    this.form.changes.subscribe(() => this.changeDetectorRef.markForCheck());
  }

  onMasterInfoChange(masterInfo: FormStateSnapshot<JstMasterInfo>): void {
    this.form.updateMasterInfo(masterInfo);
  }

  onSetAsDefault(isDefault: boolean): void {
    this.form.setAsDefault(isDefault);

    if (isDefault) {
      this.dialogService.confirmation(yesNoConfirmation('msg_spec_rfq_change_default')).pipe(
        filter$(negate(yesAnswer)))
        .subscribe(() => this.form.setAsDefault(false));
    }
  }

  onCurrentParagraphInfoChange(paragraphInfo: FormStateSnapshot<JstParagraphInfo>): void {
    const { currentParagraph } = this;

    if (currentParagraph) {
      this.form.updateParagraphInfo(currentParagraph, paragraphInfo);
    }
  }

  onCurrentSectionInfoChange(sectionInfo: FormStateSnapshot<JstSectionInfo>): void {
    const { currentSection } = this;

    if (currentSection) {
      this.form.updateSectionInfo(currentSection, sectionInfo);
    }
  }

  onCurrentSectionClausesAreChange(clausesAre: SpecTemplateClauses): void {
    const { currentSection } = this;

    if (currentSection) {
      this.form.updateSectionClausesAre(currentSection, clausesAre);
    }
  }

  onCurrentSectionClausesChange(clauses: JstClause[]): void {
    const { currentSection } = this;

    if (currentSection) {
      this.form.updateSectionClauses(currentSection, clauses);
    }
  }

  onMoveNode(): void {
    this.form.moveNodes();
  }

  onSelectNode(node: ITreeNode): void {
    switch (node.level) {
      case JobSpecTemplateLevel.Paragraph:
        this.form.selectParagraph(node.data);
        break;
      case JobSpecTemplateLevel.Section:
        this.form.selectSection(node.parent.data, node.data);
        break;
      default:
        break;
    }
  }

  onParagraphAdd(): void {
    const newParagraph = this.businessService.createNewParagraph();
    this.form.addParagraph(newParagraph);

    this.updateTree();

    this.form.selectParagraph(newParagraph);
  }

  onParagraphRemove(paragraph: JstParagraph): void {
    this.dialogService.confirmation(yesNoConfirmation('msg_spec_templ_paragraph_delete')).pipe(
      filter$(yesAnswer))
      .subscribe(() => {
        this.form.removeParagraph(paragraph);
        this.updateTree();
      });
  }

  onSectionAdd(paragraph: JstParagraph): void {
    const newSection = this.businessService.createNewSection();
    this.form.addSection(paragraph, newSection);

    this.updateTree();

    this.treeState = expandNodeById(this.treeState, paragraph.id);
    this.form.selectSection(paragraph, newSection);
  }

  onSectionRemove(paragraph: JstParagraph, section: JstSection): void {
    this.dialogService.confirmation(yesNoConfirmation('msg_spec_templ_section_delete')).pipe(
      filter$(yesAnswer))
      .subscribe(() => {
        this.form.removeSection(paragraph, section);
        this.updateTree();
      });
  }

  onResetFocusedCell(): void {
    this.form.resetFocusedCell();
  }

  goToAel(): void {
    this.navigationService.goToAel({
      entityId: this.form.id,
      entityType: 'SPEC_TEMPLATE',
      label: this.modeTitle,
    });
  }

  getParagraphTitle(paragraph: JstParagraph): string {
    return getFormStateSnapshotValue(paragraph.info).name;
  }

  getSectionTitle(section: JstSection): string {
    return getFormStateSnapshotValue(section.info).name;
  }

  private updateTitle(): void {
    const { name } = getFormStateSnapshotValue(this.masterInfo);
    const modeTitleTranslation = this.translations.get(this.modeTitle);
    this.title = {
      [FormMode.CreateMode]: modeTitleTranslation,
      [FormMode.EditMode]: `${modeTitleTranslation}: ${name}`,
      [FormMode.ViewMode]: `${modeTitleTranslation}: ${name}`,
    }[this.form.mode];
  }

  private updateTree(): void {
    this.tree.treeModel.update();
  }
}
